import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import {
  Card,
  Tag,
  Input,
  Button,
  List,
  Avatar,
  message,
  Space,
  Alert,
} from 'antd';
import {
  UserOutlined,
  SendOutlined,
  CheckOutlined,
  AlertOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { useState, useRef, useEffect } from 'react';
import { apiClient } from '../../lib/api-client';
import { playEmergencySound } from '../../lib/emergency-sound';
import { t } from '../../i18n';

interface Message {
  id: number;
  content: string;
  senderType: 'CLIENT' | 'DRIVER' | 'SUPPORT' | 'SYSTEM';
  createdAt: string;
  sender: {
    fullName: string;
  };
}

interface Ticket {
  id: number;
  subject: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  isEmergency: boolean;
  createdAt: string;
  user: {
    fullName: string;
    phone: string;
  };
  messages: Message[];
}

const statusLabels: Record<string, string> = {
  OPEN: t('support.statusOpen'),
  IN_PROGRESS: t('support.statusInProgress'),
  RESOLVED: t('support.statusResolved'),
  CLOSED: t('support.statusClosed'),
};

const statusColors: Record<string, string> = {
  OPEN: 'red',
  IN_PROGRESS: 'orange',
  RESOLVED: 'green',
  CLOSED: 'default',
};

export const EmergencyChatShow = () => {
  const { queryResult } = useShow<Ticket>();
  const { data, isLoading, refetch } = queryResult;
  const ticket = data?.data;
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<number>(0);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages?.length]);

  // Detect new incoming messages and play emergency sound
  useEffect(() => {
    const messages = ticket?.messages || [];
    if (messages.length === 0) return;

    const lastMsg = messages[messages.length - 1];
    const prevId = lastMessageIdRef.current;

    if (prevId > 0 && lastMsg.id > prevId && lastMsg.senderType !== 'SUPPORT') {
      playEmergencySound();
    }
    lastMessageIdRef.current = lastMsg.id;
  }, [ticket?.messages?.length]);

  // Auto-refresh every 10 seconds for new messages
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 10000);
    return () => clearInterval(interval);
  }, [refetch]);

  const handleReply = async () => {
    if (!replyText.trim()) return;

    setSending(true);
    try {
      await apiClient.post(`/admin/tickets/${ticket?.id}/reply`, {
        content: replyText,
      });
      message.success(t('support.replySuccess'));
      setReplyText('');
      refetch();
    } catch {
      message.error(t('common.error'));
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    try {
      await apiClient.put(`/admin/tickets/${ticket?.id}/resolve`);
      message.success(t('support.resolveSuccess'));
      refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  return (
    <Show
      isLoading={isLoading}
      title={
        <span>
          <AlertOutlined style={{ color: '#E74C3C', marginRight: 8 }} />
          {t('emergency.title')} #{ticket?.id}
        </span>
      }
      headerButtons={
        ticket?.status !== 'RESOLVED' &&
        ticket?.status !== 'CLOSED' && (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleResolve}
          >
            Hal qilish
          </Button>
        )
      }
    >
      {/* Emergency Banner */}
      <Alert
        message={t('emergency.alert')}
        description={t('emergency.alertDescription')}
        type="error"
        showIcon
        icon={<AlertOutlined />}
        style={{ marginBottom: 24 }}
      />

      {/* User Info */}
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="small">
          <div>
            <strong>{t('support.ticketUser')}:</strong>{' '}
            {ticket?.user?.fullName || t('common.noName')}
          </div>
          <div>
            <PhoneOutlined style={{ color: '#E74C3C', marginRight: 4 }} />
            <strong>{t('common.phone')}:</strong>{' '}
            <a
              href={`tel:${ticket?.user?.phone}`}
              style={{ color: '#E74C3C', fontWeight: 600, fontSize: 16 }}
            >
              {ticket?.user?.phone}
            </a>
          </div>
          <div>
            <strong>{t('common.status')}:</strong>{' '}
            <Tag color={statusColors[ticket?.status || 'OPEN']}>
              {statusLabels[ticket?.status || 'OPEN']}
            </Tag>
          </div>
          <div>
            <strong>{t('common.createdAt')}:</strong>{' '}
            {ticket?.createdAt
              ? new Date(ticket.createdAt).toLocaleString('uz-UZ')
              : '-'}
          </div>
        </Space>
      </Card>

      {/* Messages */}
      <Card title={t('support.messages')} style={{ marginBottom: 24 }}>
        <div style={{ maxHeight: 500, overflowY: 'auto', paddingRight: 8 }}>
          <List
            dataSource={ticket?.messages || []}
            renderItem={(msg: Message) => (
              <List.Item
                style={{
                  background:
                    msg.senderType === 'SUPPORT' ? '#E8F8F5' : '#FFF5F5',
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 8,
                  borderLeft:
                    msg.senderType === 'SUPPORT'
                      ? '3px solid #1ABC9C'
                      : '3px solid #E74C3C',
                }}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      icon={
                        msg.senderType === 'SUPPORT' ? (
                          <UserOutlined />
                        ) : (
                          <AlertOutlined />
                        )
                      }
                      style={{
                        backgroundColor:
                          msg.senderType === 'SUPPORT' ? '#1ABC9C' : '#E74C3C',
                      }}
                    />
                  }
                  title={
                    <Space>
                      <span>
                        {msg.senderType === 'SUPPORT'
                            ? 'Safaro Support'
                            : msg.sender?.fullName || t('support.ticketUser')}
                      </span>
                      <span style={{ color: '#7F8C8D', fontSize: 12 }}>
                        {new Date(msg.createdAt).toLocaleString('uz-UZ')}
                      </span>
                    </Space>
                  }
                  description={
                    <span style={{ fontSize: 14 }}>{msg.content}</span>
                  }
                />
              </List.Item>
            )}
          />
          <div ref={messagesEndRef} />
        </div>
      </Card>

      {/* Reply Input */}
      {ticket?.status !== 'RESOLVED' && ticket?.status !== 'CLOSED' && (
        <Card title={t('support.replySection')}>
          <Input.TextArea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={t('support.replyPlaceholder')}
            rows={4}
            style={{ marginBottom: 16 }}
            onPressEnter={(e) => {
              if (e.ctrlKey || e.metaKey) {
                handleReply();
              }
            }}
          />
          <Space>
            <Button
              type="primary"
              danger
              icon={<SendOutlined />}
              onClick={handleReply}
              loading={sending}
            >
              Yuborish
            </Button>
            <span style={{ color: '#7F8C8D', fontSize: 12 }}>
              {t('emergency.ctrlEnterHint')}
            </span>
          </Space>
        </Card>
      )}
    </Show>
  );
};
